import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Avatar, Box, Button, Typography } from "@mui/material";

import { formatLocalizedTimeAMPM } from "../../../../utils/formatDate";

import { ServiceContractStatusEnum } from "../../../GoService/enum/service.enum";

import {
  MessageSenderInfo,
  Message as MessageType,
  TextMessage as TextMessageType,
  ContractMessage as ContractMessageType,
} from "../../types/message";
import { getServiceRequestPageRoute } from "../../../../routers/routeUtils";

const Message = ({
  message,
  currentUser,
  otherUser,
  isReversed = false,
}: {
  message: MessageType;
  currentUser: MessageSenderInfo;
  otherUser: MessageSenderInfo;
  isReversed?: boolean;
}) => {
  const isCurrentUser = message.senderId === currentUser.id;
  const messageSender = isCurrentUser ? currentUser : otherUser;

  if (message.type === "TEXT")
    return (
      <TextMessage message={message} isCurrentUser={isCurrentUser} sender={messageSender} isReversed={isReversed} />
    );
  if (message.type === "CONTRACT")
    return (
      <ContractMessage message={message} isCurrentUser={isCurrentUser} sender={messageSender} isReversed={isReversed} />
    );
  return null;
};

export default Message;

const TextMessage = ({
  message,
  isCurrentUser,
  sender,
  isReversed = false,
}: {
  message: TextMessageType;
  isCurrentUser: boolean;
  sender: MessageSenderInfo;
  isReversed?: boolean;
}) => {
  const { i18n } = useTranslation();

  const { fullName, firstTwoChr, media } = sender;
  const {
    data: { text },
    createdAt,
  } = message;

  return (
    <Box sx={styles.container(isReversed, isCurrentUser)}>
      <Avatar src={media} sx={styles.userAvatar}>
        {firstTwoChr}
      </Avatar>
      <Box sx={styles.messageContainer}>
        <Typography sx={styles.messageText(isCurrentUser, isReversed)}>{text}</Typography>
        <Box sx={styles.messageSenderInfo}>
          {/* <Typography sx={styles.messageSenderName}>{fullName}</Typography> */}
          <Typography sx={styles.messageTime}>
            {formatLocalizedTimeAMPM(new Date(createdAt).toISOString(), i18n.language)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const ContractMessage = ({
  message,
  isCurrentUser,
  sender,
  isReversed,
}: {
  message: ContractMessageType;
  isCurrentUser: boolean;
  sender: MessageSenderInfo;
  isReversed?: boolean;
}) => {
  const { i18n, t } = useTranslation("chat", { keyPrefix: "message.contract" });
  const { fullName, firstTwoChr, media } = sender;
  const { data: serviceContract, createdAt } = message;
  const {
    contract: {
      serviceRequestId,
      status,
      paymentConfig: { amount, currency, type },
      milestones,
    },
  } = serviceContract;

  const totalContractAmount =
    type === "fixed" ? amount / 100 : (amount / 100) * (milestones?.length ? milestones?.length : 1);
  const numberOfServicesDelivered = milestones?.length ? milestones?.length : 1;

  const navigate = useNavigate();
  const handleViewDetails = () => navigate(getServiceRequestPageRoute(serviceRequestId, "contract"));

  return (
    <Box sx={styles.container(isReversed, isCurrentUser)}>
      <Avatar src={media} sx={styles.userAvatar}>
        {firstTwoChr}
      </Avatar>
      <Box sx={styles.messageContainer}>
        <Typography sx={styles.contractState(status)}>{t(`contractState.${status}`)}</Typography>
        <Box sx={styles.contractContainer(status)}>
          <Typography sx={styles.details}>
            {t("totalContractPrice", { price: totalContractAmount, currency })}
          </Typography>
          <Typography sx={styles.details}>
            {t("numberOfServicesDelivery", { numberOfServicesDelivered: numberOfServicesDelivered })}
          </Typography>
          <Typography sx={styles.details}>
            {t("amountInEscrow", { amountInEscrow: totalContractAmount, currency })}
          </Typography>
          <Button variant="text-sm" onClick={handleViewDetails} sx={styles.viewDetailsButton(status)}>
            {t("viewDetails")}
          </Button>
        </Box>
        <Box sx={styles.messageSenderInfo}>
          {/* <Typography sx={styles.messageSenderName}>{fullName}</Typography> */}
          <Typography sx={styles.messageTime}>
            {formatLocalizedTimeAMPM(new Date(createdAt).toISOString(), i18n.language)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  container: (isReversed?: boolean, isCurrentUser?: boolean) => ({
    display: "flex",
    gap: "12px",
    // Current user messages: avatar on left, message on right (row)
    // Other user messages: avatar on right, message on left (row-reverse)
    // When RTL (isReversed), flip the logic
    flexDirection: isCurrentUser ? (isReversed ? "row-reverse" : "row") : isReversed ? "row" : "row-reverse",
  }),
  messageContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  userAvatar: {
    width: "40px",
    height: "40px",
  },
  messageContent: {
    display: "flex",
    flexDirection: "column",
  },
  messageSenderName: {
    color: "var(--Gray-700, #344054)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
  },
  messageText: (isCurrentUser: boolean, isReversed: boolean) => ({
    fontFamily: "Montserrat",
    color: isCurrentUser ? "var(--White, #FFF)" : "var(--Gray-900, #101828)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "150%",
    borderRadius: isReversed ? "0 8px 8px 8px" : "8px 0 8px 8px",
    background: isCurrentUser ? "var(--Brand-600, #008B07)" : "var(--Gray-100, #F2F4F7)",
    padding: "10px 14px",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  }),
  messageSenderInfo: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "80px",
  },
  messageTime: {
    fontFamily: "Montserrat",
    color: "var(--Gray-600, #475467)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
  },
  contractState: (status: ServiceContractStatusEnum) => ({
    fontFamily: "Montserrat",
    color:
      status === ServiceContractStatusEnum.Approved
        ? "var(--Brand-600, #008B07)"
        : status === ServiceContractStatusEnum.Pending
        ? "var(--Gray-600, #475467)"
        : "var(--Red---200, #CC3E3E)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
  }),
  contractContainer: (status: ServiceContractStatusEnum) => ({
    paddingLeft: "16px",
    position: "relative",
    background:
      status === ServiceContractStatusEnum.Approved
        ? "var(--Green---25, #E7EFED);"
        : status === ServiceContractStatusEnum.Pending
        ? "var(--Gray-100, #F2F4F7)"
        : "var(--Red---25, #FFEDED)",
    borderRadius: "0 8px 8px 8px",
    padding: "10px 14px 10px 26px",

    "&::before": {
      content: '""',
      position: "absolute",
      left: "16px",
      top: "10px",
      width: "2px",
      height: "calc(100% - 20px)",
      background:
        status === ServiceContractStatusEnum.Approved
          ? "var(--Brand-600, #008B07)"
          : status === ServiceContractStatusEnum.Pending
          ? "var(--Gray-600, #475467)"
          : "var(--Red---200, #CC3E3E)",
    },
  }),
  details: {
    fontFamily: "Montserrat",
    color: "var(--Gray-900, #101828)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
  viewDetailsButton: (status: ServiceContractStatusEnum) => ({
    background: "transparent !important",
    width: "fit-content",
    color:
      status === ServiceContractStatusEnum.Approved
        ? "var(--Brand-600, #008B07) !important"
        : status === ServiceContractStatusEnum.Pending
        ? "var(--Gray-600, #475467) !important"
        : "var(--Red---200, #CC3E3E) !important",
  }),
};
